import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { Alert, Snackbar, type SnackbarOrigin } from '@mui/material';

import {
  NotificationContext,
  type NotificationContextType,
} from '../../contexts/NotificationContext/NotificationContext';

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  key: number;
  message: string;
  severity: NotificationSeverity;
  duration?: number;
  position?: SnackbarOrigin;
}
export interface NotificationProviderProps {
  children: ReactNode;
  defaultPosition?: SnackbarOrigin;
  defaultDuration?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  defaultPosition = { vertical: 'top', horizontal: 'right' }, // Default position
  defaultDuration = 3000, // Default duration
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const show: NotificationContextType['show'] = useCallback(
    (message, options = {}) => {
      const key = Date.now(); // Unique key
      setNotifications((prev) => [
        ...prev,
        {
          key,
          message,
          severity: options.severity || 'info',
          duration: options.autoHideDuration ?? defaultDuration,
          position: options.position ?? defaultPosition,
        },
      ]);
      return key;
    },
    [defaultDuration, defaultPosition],
  );

  const close = useCallback((key: number) => {
    setNotifications((prev) => prev.filter((notif) => notif.key !== key));
  }, []);

  const contextValue = useMemo(() => ({ show, close }), [show, close]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notifications.map((notif) => (
        <Snackbar
          key={notif.key}
          open={true}
          {...{
            autoHideDuration: notif.duration,
            onClose: () => close(notif.key),
            anchorOrigin: notif.position ?? defaultPosition,
          }}
        >
          <Alert
            onClose={() => close(notif.key)}
            severity={notif.severity}
            variant="filled"
            {...{ children: notif.message }}
          />
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};
